import {
  useGetSuggestedSearchByIdQuery,
  useUpdateSuggestedSearchMutation,
} from "@/app/features/suggestedSearch/suggestedSearchApiSlice";
import FormInput from "@/components/forms/FormInput";
import FormSelect from "@/components/forms/FormSelect";
import {
  Box,
  Button,
  Container,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  searchType: Yup.string().oneOf(["global", "ai"]).required().label("Type"),
  label: Yup.string().required().max(100).label("Label").trim(),
  searchQuery: Yup.string().required().max(200).label("Search query").trim(),
  sortOrder: Yup.number().min(0).label("Sort order"),
  isActive: Yup.string().required().label("Active"),
});

const typeItems = [
  { name: "Global search (header + /search page)", code: "global" },
  { name: "AI search (Find a Car)", code: "ai" },
];

const activeItems = [
  { name: "Yes", code: "true" },
  { name: "No", code: "false" },
];

export default function EditSuggestedSearchPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useGetSuggestedSearchByIdQuery(
    params.id
  );
  const [updateSuggestedSearch, { isLoading: isUpdating }] =
    useUpdateSuggestedSearchMutation();

  const item = data?.data?.suggestedSearch;

  const handleSubmit = async (inputs) => {
    try {
      const result = await updateSuggestedSearch({
        id: params.id,
        data: {
          searchType: inputs.searchType,
          label: inputs.label.trim(),
          searchQuery: inputs.searchQuery.trim(),
          sortOrder: Number(inputs.sortOrder) || 0,
          isActive: inputs.isActive === "true",
        },
      });

      if (result?.data) {
        toast.success(result.data.message);
        navigate("/suggested-search");
      } else if (result?.error) {
        toast.error(result.error?.data?.message || "Failed to update");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    }
  };

  if (isLoading) {
    return (
      <Skeleton
        sx={{ transform: "scale(1, 1.0)", transformOrigin: "0% 0%" }}
        width="100%"
        height="600px"
      />
    );
  }

  if (isError) {
    return <p>{error?.data?.message || "Something went wrong"}</p>;
  }

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Typography variant="h4">Edit Suggested Search</Typography>

          <Formik
            enableReinitialize
            initialValues={{
              searchType: item?.searchType === "ai" ? "ai" : "global",
              label: item?.label || "",
              searchQuery: item?.searchQuery || "",
              sortOrder: item?.sortOrder ?? 0,
              isActive: item?.isActive === false ? "false" : "true",
            }}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {() => (
              <Form>
                <Stack spacing={3}>
                  <FormSelect
                    label="Search type"
                    name="searchType"
                    items={typeItems}
                  />
                  <FormInput
                    label="Label (shown in dropdown)"
                    name="label"
                    type="text"
                    placeholder="e.g. Electric SUV"
                  />
                  <FormInput
                    label="Search query (used when clicked)"
                    name="searchQuery"
                    type="text"
                    placeholder="e.g. Electric SUV"
                  />
                  <FormInput
                    label="Sort order (lower = first)"
                    name="sortOrder"
                    type="number"
                    placeholder="0"
                  />
                  <FormSelect label="Active" name="isActive" items={activeItems} />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isUpdating}
                  >
                    Submit
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Container>
    </Box>
  );
}
